import { BaseCubiqLogger } from "shared/broker-client-services/worker/BaseCubiqLogger";
import { RESPONSE_LOG_PREFIX } from "shared/broker-client-services/worker/CubiqLogger.constants";
import { CubiqLoggerConfig } from "shared/broker-client-services/worker/CubiqLoggerConfig";
import {
  AdminModelUpdateImpl,
  BMTimeCodeImpl,
  DataProfileResponseImpl,
  FixingStatusResponseImpl,
  FwdRollChangeResponseImpl,
  LinkedOrderRequestImpl,
  ManualNettingResponseImpl,
  MarketDataEventImpl,
  OpsExcInfoImpl,
  OpsFilterCompositeDataImpl,
  PingTimerImpl,
  ResponseCode,
  SavedOrderEventListImpl,
  ServiceResponseImpl,
  StandardReportImpl,
  StaticDataProfileImpl,
  StrategyFillLockResponseImpl,
  SystemMonitorEventImpl,
  SystemMonitorEventSetImpl,
  TelemetrySubscriptionCatalogueImpl,
  TradingCalendarSnapShotImpl,
} from "shared/cubiq-model-multi-asset";
import {
  SYSMON_EVENT_FQDN,
  SYSMON_EVENT_FQDN_DISP_TXT,
} from "shared/cubiq-services/lookup/model/impl/SystemMonitorLookup.constants";

type ItemRendererType = (row: any) => string;
type ListRendererType = (rows: any[]) => string;

/**
 * Cubiq logger for responses to UI requests
 *
 * By default shares a common config with other loggers - message logging can be enabled/disabled by type
 * based on the message encoding type
 */

export class CubiqResponseLogger extends BaseCubiqLogger {
  private outputServiceResponse: boolean = true;

  /** Map of encodableType->renderer used to map a list of elements of that type to an output string  */
  private reportRowRendererMap: Map<string, ListRendererType> = new Map();

  // properties to enable/disable specifc events

  constructor(config: CubiqLoggerConfig) {
    super(RESPONSE_LOG_PREFIX, config);
  }

  /**
   * Process a single repsonse that has arrived wrapped in a ServiceResponseImpl
   *
   * @param response
   * @returns
   */
  public processResponse(response: ServiceResponseImpl) {
    if (this.outputServiceResponse) {
      super.updateMessageId(response);

      // Translate the payload - if not null, then log
      let payloadMessage: string = "";
      let payloadType = response.payload?.encodableType;
      if (payloadType) {
        payloadType = this.getLastClassPart(payloadType);
        payloadMessage = this.getPayloadMessage(response.serviceRequestType, payloadType, response);
        if (payloadMessage == null) {
          // Ignored
          return;
        }
      } else {
        payloadMessage = "<NO PAYLOAD>";
      }

      this.updateMessageId(response);

      const errors = this.getErrorMessages(response.errorList).join("\n");
      const errorMsg = errors.length > 0 ? ` Errors: ${errors}` : "";
      let responseCode: string = "";
      if (response.responseCode !== ResponseCode.COMPLETE) {
        responseCode = ` - ${ResponseCode[response.responseCode]}`;
      }
      this.log(
        `ServiceResponse: (${response.serviceRequestType}${responseCode}) => ${payloadMessage}${errorMsg}`,
      );
    }
  }

  /**
   * Creates the message used to describe the response payload
   *
   * A return value of null will abort outputting of entire service repsonse
   *
   * @param serviceRequestType
   * @param payloadType
   * @param response
   * @returns
   */
  private getPayloadMessage(
    serviceRequestType: string,
    payloadType: string,
    response: ServiceResponseImpl,
  ): string {
    let retValue: string = payloadType;

    if (!this.config.outputResponseType(serviceRequestType)) {
      return null;
    }

    // Response handling can vary based on requestType as well as response type but
    // for the moment, we will treat all instances of a responseType equally
    switch (payloadType) {
      // Add any payload-specific logging here and return
      case "DataProfileResponseImpl":
        retValue = this.getDataProfileResponseImpl(
          response.payload as DataProfileResponseImpl,
          serviceRequestType,
        );
        break;
      case "StrategyFillLockResponseImpl":
        retValue = this.getStrategyFillLockResponse(
          response.payload as StrategyFillLockResponseImpl,
        );
        break;
      case "StandardReportImpl":
        retValue = this.getStandardReportResponse(
          response.payload as StandardReportImpl,
          serviceRequestType,
        );
        break;
      case "StaticDataProfileImpl":
        retValue = this.getStaticDataProfileImpl(response.payload as StaticDataProfileImpl);
        break;
      case "ManualNettingResponseImpl":
        retValue = this.getManualNettingResponseImpl(response.payload as ManualNettingResponseImpl);
        break;
      case "TrustToken":
      case "PingTimerImpl":
        // Just use default reporting of the type
        break;
      case "TelemetrySubscriptionCatalogueImpl":
        retValue = this.getTelemetrySubscriptionCatalogueImpl(
          response.payload as TelemetrySubscriptionCatalogueImpl,
        );
        break;
      case "OpsFilterCompositeDataImpl":
        retValue = this.getOpsFilterCompositeDataImpl(
          response.payload as OpsFilterCompositeDataImpl,
        );
        break;
      case "SavedOrderEventListImpl":
        retValue = this.getSavedOrderEventListImpl(response.payload as SavedOrderEventListImpl);
        break;
      case "SystemMonitorEventSetImpl":
        retValue = this.getSystemMonitorEventSetImpl(response.payload as SystemMonitorEventSetImpl);
        break;
      case "FixingStatusResponseImpl":
        retValue = this.getFixingStatusResponseImpl(response.payload as FixingStatusResponseImpl);
        break;
      case "TradingCalendarSnapShotImpl":
        retValue = this.getTradingCalendarSnapShotImpl(
          response.payload as TradingCalendarSnapShotImpl,
        );
        break;
      case "MarketDataEventImpl":
        retValue = this.getMarketDataEventImpl(response.payload as MarketDataEventImpl);
        break;
      case "FwdRollChangeResponseImpl":
        retValue = this.getFwdRollChangeResponseImpl(response.payload as FwdRollChangeResponseImpl);
        break;
      default:
        if (this.logUnsupported("Response", payloadType)) {
          console.log(
            `case "${payloadType}": retValue = this.get${payloadType}( response.payload as ${payloadType});break;`,
          );
          console.log(
            `private get${payloadType}(payload:${payloadType}):string { return \`${payloadType}\`;}`,
          );
        }
        break;
    }
    return retValue;
  }
  getFwdRollChangeResponseImpl(response: FwdRollChangeResponseImpl): string {
    return `FwdRollChangeResponseImpl`;
  }

  /**
   * Log out received static data - ignores non-array parts
   * @param response
   * @returns
   */
  private getStaticDataProfileImpl(response: StaticDataProfileImpl) {
    const parts: string[] = [];

    for (const property in response) {
      const value = response[property];
      if (Array.isArray(value) && value.length > 0) {
        parts.push(`${property}[${value.length}]`);
      }
    }

    return `StaticDataProfileImpl(${response.systemVersion}) - Provided: ${parts.join(",")}`;
  }

  private getDataProfileResponseImpl(
    response: DataProfileResponseImpl,
    serviceRequestType: string,
  ) {
    const parts: string[] = [];

    for (const data of response.responseData) {
      parts.push(this.getStandardReportResponse(data as StandardReportImpl, serviceRequestType));
    }
    return `DataProfileResponseImpl - Provided: ${parts.join("\n")}`;
  }

  /**
   * We have a list repsonse from the server - need to do quite a a bit of work to
   * get some useful info from that.
   * This creates a rendering function based on the payload type and the returns
   * @param response
   * @param _serviceRequestType
   * @returns
   */
  private getStandardReportResponse(
    response: StandardReportImpl,
    _serviceRequestType: string,
  ): string {
    const payload = response.results;
    if (payload?.length > 0) {
      // First, this assumes that all list items are the same time so we pick the first item to introspect.

      const firstRow = payload[0];
      const rowType =
        "encodableType" in firstRow ? this.getLastClassPart(firstRow.encodableType) : "Unknown";

      // Get a renderer function for the list item type
      if (!this.reportRowRendererMap.has(rowType)) {
        const renderer = this.getRendererForRowType(rowType, firstRow);
        this.reportRowRendererMap.set(rowType, renderer);
      }

      const renderer = this.reportRowRendererMap.get(rowType);
      return renderer(payload);
    } else {
      return "<Empty List>";
    }
  }

  private getStrategyFillLockResponse(response: StrategyFillLockResponseImpl): string {
    if (response.fillData) {
      const parts: string[] = response.fillData.map((fillData) => `${fillData.orderId}`);
      return `StrategyFillLockResponse - orders[${parts.join(",")}]`;
    }
    return "";
  }

  private getManualNettingResponseImpl(response: ManualNettingResponseImpl): string {
    return `ManualNettingResponse - success (${response.success})`;
  }

  private getTelemetrySubscriptionCatalogueImpl(
    payload: TelemetrySubscriptionCatalogueImpl,
  ): string {
    const partCount = payload.catalogueEntries?.length ?? 0;
    return `TelemetrySubscriptionCatalogueImpl - catalog entry count=${partCount}, channel=${payload.consumerChannel}`;
  }

  private getOpsFilterCompositeDataImpl(payload: OpsFilterCompositeDataImpl): string {
    const parts: string[] = payload.opsFilterCategoryTypes.map(
      (category) => category.filterCatDesc,
    );
    const typeCount = payload.opsFilterCategories?.length ?? 0;
    return `OpsFilterCompositeDataImpl - categories[${parts.join(",")}], ${typeCount} total types`;
  }
  private getSavedOrderEventListImpl(payload: SavedOrderEventListImpl): string {
    const limitOrders: string[] =
      payload.orderDataRequests?.map((request) => request.rootOrder?.orderId ?? "<Unknown>") ?? [];
    const fixingOrders: string[] =
      payload.fixingOrderRequests?.map(
        (request) => request.fixingOrderData?.orderId ?? "<Unknown>",
      ) ?? [];

    const limitDesc = `${limitOrders.length} orders`;
    const fixingDesc = `${fixingOrders.length} orders`;
    return `SavedOrderEventListImpl - Limit Orders: ${limitDesc}, BM Orders: ${fixingDesc}`;
  }

  private getSystemMonitorEventSetImpl(payload: SystemMonitorEventSetImpl): string {
    const name = payload.fQDN ?? "<Unknown>";
    return `SystemMonitorEventSetImpl - fqdn='${name}'`;
  }

  private getFixingStatusResponseImpl(payload: FixingStatusResponseImpl): string {
    const statusCount = payload.fixingPositionStatus?.length ?? 0;
    const fixingEventCount = payload.fixingEvent.length ?? 0;
    const manHedgingCount = payload.manualHedgingPositions.length ?? 0;
    const positionCount = payload.benchmarkPositionAudits.length ?? 0;

    return `FixingStatusResponseImpl - fixingPositionStatus[${statusCount}], fixingEvent[${fixingEventCount}], manualHedgingPositions[]${manHedgingCount}, benchmarkPositionAudits[${positionCount}]`;
  }

  private getTradingCalendarSnapShotImpl(payload: TradingCalendarSnapShotImpl): string {
    const runCount = payload.calendarRun?.length ?? 0;
    return `TradingCalendarSnapShotImpl - calendarRun[${runCount}]`;
  }

  private getMarketDataEventImpl(payload: MarketDataEventImpl): string {
    const parts: string[] = [];
    parts.push(payload.subscriptionCode);
    parts.push(payload.providerCode);
    parts.push(payload.rateSource);
    const partsText = parts.join(" ");
    return `MarketDataEventImpl - ${partsText}`;
  }

  // Helper methods

  /**
   * Return a function that will map a list of items (with specified encodable type) to a string
   * @param rowType
   * @param firstRow
   * @returns
   */
  private getRendererForRowType(rowType: string, firstRow: any): ListRendererType {
    switch (rowType) {
      case "BlotterDefinitionImpl":
        // For blotters, just want to see the blotter Ids
        return this.createIdRenderer(rowType, "blotterName", -1);
        break;
      case "BMTimeCodeImpl":
        return this.createIdRenderer(rowType, "code", 101);
        break;
      case "PositionBookImpl":
        // For position books, just want to see the book names
        return this.createIdRenderer(rowType, "positionBookName", -1);
        break;
      case "BlotterFilterImpl":
        // For bloptter filters just return list of filter Ids
        return this.createIdRenderer(rowType, "filterName", -1);
        break;
      case "OrderAuditImpl":
        // For Order Audits - can be huge
        return this.createIdRenderer(rowType, "filterName", 0);
        break;
      case "InterventionEventImpl":
        return this.createIdRenderer(rowType, "eventId", 0);
        break;
      case "DealImpl":
        return this.createIdRenderer(rowType, "id", 101);
        break;
      case "OrderDataImpl":
        return this.createIdRenderer(rowType, "orderId", 101);
        break;
      case "SystemMonitorEventImpl":
        return this.createSystemMonitorEventRenderer(101);
      case "OpsExcInfoImpl":
        return this.createOpsExceptionRenderer(101);
      case "AdminModelUpdateImpl":
        return this.createAdminModelUpdateRenderer();
      default:
        if (this.config.warnUnsupportedResponseMember) {
          this.logUnsupported("Response-List", rowType);
        }
        return this.createDefaultRenderer(rowType, firstRow, 101);
    }
  }

  private createAdminModelUpdateRenderer(): ListRendererType {
    return (rows) => {
      let rowCount = `${rows.length}`;
      let firstType;
      const firstRow = rows[0];
      if (firstRow instanceof AdminModelUpdateImpl) {
        firstType = firstRow.entityType;
      } else {
        firstType = "<Unknown>";
      }

      return `LIST:AdminModelUpdateImpl[${rowCount}] - ${firstType}]`;
    };
  }

  private createSystemMonitorEventRenderer(maxCount: number): ListRendererType {
    return (rows) => {
      let rowCount = `${rows.length}`;
      const constrained = maxCount >= 0 && rows.length > maxCount;
      const truncatedText = constrained ? `[TRUNCATED - ${maxCount}]` : "";
      const rowsToProcess = constrained ? rows.slice(0, maxCount) : rows;
      const parts: string[] = rowsToProcess.map(
        (event: SystemMonitorEventImpl) =>
          event.dataMap?.get(SYSMON_EVENT_FQDN_DISP_TXT) ?? "<Unknown",
      );

      return `LIST:SystemMonitorEventImpl[${rowCount}] - ${truncatedText} [${parts.join(",")}]`;
    };
  }

  private createOpsExceptionRenderer(maxCount: number): ListRendererType {
    return (rows) => {
      let rowCount = `${rows.length}`;
      const constrained = maxCount >= 0 && rows.length > maxCount;
      const truncatedText = constrained ? `[TRUNCATED - ${maxCount}]` : "";
      const rowsToProcess = constrained ? rows.slice(0, maxCount) : rows;
      const parts: string[] = rowsToProcess.map((event: OpsExcInfoImpl) => {
        const opsId = `Ops[${event.exceptionId.toString()}]`;
        let id: string;
        if (event.orderData) {
          id = `order[${event.orderData.orderId.toString()} ${opsId}]`;
        } else if (event.deal) {
          id = `deal[${event.deal.id.toString()} ${opsId}]`;
        } else {
          id = opsId;
        }
        return id;
      });

      return `LIST:OpsExcInfoImpl[${rowCount}] - ${truncatedText} [${parts.join(",")}]`;
    };
  }

  /**
   * Attempt to figure out a suitable rendering function for an unknown report object type. If
   * no ID is found in the entity, it will return a default representation of the list with type and count only
   *
   * @param rowType
   * @param firstRow
   * @returns
   */
  private createDefaultRenderer(
    rowType: string,
    firstRow: any,
    maxCount: number,
  ): ListRendererType {
    if ("id" in firstRow) {
      return this.createIdRenderer(rowType, "id", maxCount);
    } else if ("orderId" in firstRow) {
      return this.createIdRenderer(rowType, "orderId", maxCount);
    } else if ("dealId" in firstRow) {
      return this.createIdRenderer(rowType, "dealId", maxCount);
    }
    return this.createUnknownRenderer(rowType);
  }

  /**
   * Return a list rendering function that will show all the members of the list using
   * the specified ids
   * @param rowType The Encodable type of the list items
   * @param idName The field name used to get the list item ID
   * @param idLabel The overall label to use for the entire list
   * @param maxCount Max number of list rows to process - use -1 to ignore, 0 will output no rows
   * @returns
   */
  private createIdRenderer(rowType: string, idName: string, maxCount: number): ListRendererType {
    return this.createGeneralRenderer(rowType, (row) => row[idName], maxCount);
  }

  /**
   * Return a list rendering function that will show all the members of the list using the specified rendering method
   *
   * @param rowType
   * @param rowRenderer
   * @param maxCount Max number of list rows to process - use -1 to ignore, 0 will output no rows
   * @param joinStr String used to concatenate members of the list, defaults to ','
   * @returns
   */
  private createGeneralRenderer(
    rowType: string,
    rowRenderer: ItemRendererType,
    maxCount: number,
    joinStr: string = ",",
  ): ListRendererType {
    return (rows) => {
      let rowCount = `${rows.length}`;
      const constrained = maxCount >= 0 && rows.length > maxCount;
      const truncatedText = constrained ? `[TRUNCATED - ${maxCount}]` : "";
      const rowsToProcess = constrained ? rows.slice(0, maxCount) : rows;
      const parts: string[] = rowsToProcess.map(rowRenderer);

      return `LIST:${rowType}[${rowCount}] - ${truncatedText} [${parts.join(joinStr)}]`;
    };
  }

  /**
   * Return renderer for a list of items that have no known formatting renderer
   * @param rowType
   * @returns
   */
  private createUnknownRenderer(rowType: string) {
    return (rows) => {
      let rowCount = `${rows.length}`;
      return `LIST:${rowType}[${rowCount}] - Unknown Ids`;
    };
  }

  /**
   * Return a list rendering function that will not log anything for the Report
   * @returns
   */
  private createNullRenderer(): ListRendererType {
    return (_rows) => null;
  }
}
